import React, { useCallback, useState } from "react";
import { Link, useParams, withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import fidoPasskeyLogo from "../assets/images/fido-passkey-logo.png";
import api from "../utils/api";
import FieldGroup from "../shared/forms/layout/FieldGroup";
import UnauthenticatedLayout from "../shared/layout/UnauthenticatedLayout";
import styles from "./Login.module.css";
import { v4 as uuidv4 } from "uuid";

function base64URLToBuffer(base64URL) {
  const base64 = base64URL.replace(/-/g, "+").replace(/_/g, "/");
  const padLen = (4 - (base64.length % 4)) % 4;
  return Uint8Array.from(atob(base64.padEnd(base64.length + padLen, "=")), c =>
    c.charCodeAt(0)
  );
}

const Login = props => {
  const { history, onLogin } = props;
  const { origin } = useParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  const deviceId = uuidv4();

  const handleLoginSuccess = useCallback(
    async user => {
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("deviceId", JSON.stringify(deviceId));

      const customer = await api.get(
        `customer/${user.customerId}`,
        null,
        history
      );
      if (customer) {
        user.customer = customer;
        localStorage.setItem("user", JSON.stringify(user));
      }
      onLogin(user);
      history.push(`/${t("sessions")}`);
    },
    [deviceId, history, onLogin, t]
  );

  const login = useCallback(async () => {
    const options = {
      body: JSON.stringify({
        Username: username,
        Password: password,
        DeviceId: deviceId
      })
    };

    try {
      const user = await api.post("authenticate", options, history);
      if (user?.token) {
        handleLoginSuccess(user);
      } else {
        setError(
          `${t("The login attempt failed.")}\n ${t(
            "Double check your email and password."
          )}`
        );
      }
    } catch (err) {
      console.log("Error login:", err);
    }
  }, [username, password, t, history, deviceId, handleLoginSuccess]);

  const passkeyLogin = async () => {
    const assertionOptions = await api.get(
      `fido2/assertion-options`,
      undefined,
      history
    );
    assertionOptions.challenge = base64URLToBuffer(assertionOptions?.challenge);
    const assertionCredentials = await navigator.credentials.get({
      publicKey: assertionOptions
    });

    try {
      const options = { body: JSON.stringify(assertionCredentials) };
      const user = await api.post(
        `fido2/assertion/${deviceId}`,
        options,
        history
      );
      if (user?.token) {
        handleLoginSuccess(user);
      } else {
        setError(
          `${t("The passkey attempt failed.")}\n ${t(
            "Double check your passkey configuration."
          )}`
        );
      }
    } catch (err) {
      console.log("Error during passkey login:", err);
    }
  };

  const onKeyPress = useCallback(
    e => {
      if (e.key === "Enter") login();
    },
    [login]
  );
  const originMessage = origin
    ? origin === "reset"
      ? "Your password was reset successfully."
      : ""
    : "";

  return (
    <UnauthenticatedLayout>
      <div className={styles.form}>
        <FieldGroup className={styles.fields}>
          {originMessage && (
            <Typography align="left" variant="body1">
              {t(originMessage)}
            </Typography>
          )}
          <TextField
            label={t("Email Address")}
            onChange={e => setUsername(e.target.value)}
            onKeyPress={onKeyPress}
            variant="filled"
            InputLabelProps={{ style: { color: "white" } }}
          />
          <TextField
            inputProps={{ type: "password" }}
            label={t("Password")}
            onChange={e => setPassword(e.target.value)}
            onKeyPress={onKeyPress}
            variant="filled"
            InputLabelProps={{ style: { color: "white" } }}
          />
        </FieldGroup>
        <div className={styles.forgotPassword}>
          <Link className={styles.link} to="/request-password-reset">
            <Typography variant="body2">{t("forgot password")}</Typography>
          </Link>
        </div>
        {error && (
          <Typography align="left" variant="body1" className={styles.error}>
            {error}
          </Typography>
        )}
        <FieldGroup className={styles.buttonWrapper}>
          <Button color="primary" onClick={login} variant="contained">
            {t("Login")}
          </Button>
        </FieldGroup>
      </div>
      <img
        src={fidoPasskeyLogo}
        alt="Fido Passkey Logo"
        className={styles.keyIcon}
        onClick={passkeyLogin}
      />
    </UnauthenticatedLayout>
  );
};

export default withRouter(Login);
